import { useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/cn';
import { tenantQuery } from '@/queries';

/**
 * The tenant wordmark.
 *
 * It reads `brand.name` from `tenant_config` now. Until R-140 it said "The
 * Embroidery Shop", the previous client, on every page of this client's system,
 * while the footer two hundred pixels below said "Obsessed Gymwear". The table
 * held the right answer the whole time and nothing read it.
 *
 * It draws the supplied logo when `brand.logo_url` holds one, and falls back to
 * type when it does not. The fallback is not decoration: this system runs for
 * more than one tenant, and a tenant with no logo yet must still have a header
 * (FR-003, task T025b).
 *
 * The supplied artwork is black on transparency, which is invisible on the dark
 * theme. `.logo-mark` inverts it there, in globals.css, next to the rule that
 * does the same job for accent text. If the client supplies a light version,
 * drop the class and use two files.
 */
export function Wordmark({ className, href = '/' }: { className?: string; href?: string }) {
  const tenant = useSuspenseQuery(tenantQuery).data;

  // The last word carries the accent, which is how the mark was drawn. A
  // one-word name keeps its whole self in the accent rather than losing it.
  const words = tenant.name.trim().split(/\s+/);
  const lead = words.slice(0, -1).join(' ');
  const last = words[words.length - 1];

  if (tenant.logo_url) {
    return (
      <Link to={href} className={cn('inline-flex items-center', className)}>
        <img
          src={tenant.logo_url}
          // The name, not the word "logo": a reader who cannot see the image
          // needs to know whose shop this is.
          alt={tenant.name}
          className="logo-mark h-7 w-auto sm:h-8"
        />
        <span className="sr-only">home</span>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className={cn(
        'inline-flex items-center gap-2 text-[19px] font-extrabold tracking-[-0.03em]',
        className,
      )}
    >
      <svg
        width="24"
        height="18"
        viewBox="0 0 30 22"
        aria-hidden="true"
        className="shrink-0"
        fill="none"
        stroke="var(--color-brand)"
        strokeWidth="2.6"
        strokeLinecap="round"
      >
        <path d="M2 6c4-3.6 8-3.6 12 0s8 3.6 12 0" />
        <path d="M2 12c4-3.6 8-3.6 12 0s8 3.6 12 0" />
        <path d="M2 18c4-3.6 8-3.6 12 0s8 3.6 12 0" />
      </svg>
      <span className="font-[family-name:var(--font-head)] uppercase">
        {lead ? `${lead} ` : ''}
        <span className="text-brand-ink">{last}</span>
      </span>
      <span className="sr-only">home</span>
    </Link>
  );
}
